import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, Image, message, Modal, Space, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import PageTab from "@/components/i-pageTab/i-pageTab";
import PageTable from "@/components/i-pageTable/i-pageTable";
import {
  editCustomerFeedback,
  selectCmsCustomerFeedbackPage,
} from "@/api/other";
import type {
  CustomerFeedbackRow,
  EditCustomerFeedbackParams,
  SelectCmsCustomerFeedbackPageParams,
} from "@/api/other";
import { setIcons } from "@/utils/common";

const { Paragraph } = Typography;

interface DetailTextCellProps {
  title: string;
  value: string;
  onView: (title: string, value: string) => void;
}

const DetailTextCell = ({
  title,
  value,
  onView,
}: DetailTextCellProps): React.ReactElement => {
  const [isEllipsis, setIsEllipsis] = useState<boolean>(false);

  const handleView = useCallback((): void => {
    onView(title, value);
  }, [onView, title, value]);

  if (!value) {
    return <span>-</span>;
  }

  return (
    <div style={{ width: 240 }}>
      <Paragraph
        ellipsis={{ rows: 2, onEllipsis: setIsEllipsis }}
        style={{
          marginBottom: 0,
          overflowWrap: "anywhere",
          whiteSpace: "pre-wrap",
        }}
      >
        {value}
      </Paragraph>
      {isEllipsis && (
        <Button
          size="small"
          style={{ height: "auto", padding: 0 }}
          type="link"
          onClick={handleView}
        >
          查看详情
        </Button>
      )}
    </div>
  );
};

const responseTypeOptions = [
  { label: "电话", value: 0 },
  { label: "留言", value: 1 },
];

const statusOptions = [
  { label: "待处理", value: 0 },
  { label: "已处理", value: 1 },
];

const initialFormData: SelectCmsCustomerFeedbackPageParams = {
  endTime: "",
  mobile: "",
  nickName: "",
  startTime: "",
  status: undefined,
  ypageNum: 1,
  ypageSize: 10,
};

const getImageUrl = (item: unknown): string => {
  if (typeof item === "string") {
    return item;
  }
  if (!item || typeof item !== "object") {
    return "";
  }

  const image = item as Record<string, unknown>;
  const url = image.url ?? image.imgUrl ?? image.imageUrl ?? image.fileUrl;
  return typeof url === "string" ? url : "";
};

const parseImageUrls = (row: CustomerFeedbackRow): string[] => {
  const listUrls = (row.customerFeedbackImgList ?? [])
    .map(getImageUrl)
    .filter(Boolean);
  if (listUrls.length > 0) {
    return listUrls;
  }

  const imageValue = row.customerFeedbackImgs?.trim();
  if (!imageValue) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(imageValue);
    if (Array.isArray(parsed)) {
      return parsed.map(getImageUrl).filter(Boolean);
    }
  } catch {
    return imageValue
      .split(",")
      .map((url) => url.trim())
      .filter(Boolean);
  }

  return [];
};

const formatTime = (value: unknown): string => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return "-";
};

const CustomerFeedbackManager = (): React.ReactElement => {
  const [formData, setFormData] =
    useState<SelectCmsCustomerFeedbackPageParams>(initialFormData);
  const [pageData, setPageData] = useState<CustomerFeedbackRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalLoading, setModalLoading] = useState<boolean>(false);
  const [editingFeedback, setEditingFeedback] =
    useState<EditCustomerFeedbackParams>({});

  const getPageData = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await selectCmsCustomerFeedbackPage(formData);
      setPageData(data.rows ?? []);
      setTotal(data.total ?? 0);
    } finally {
      setLoading(false);
    }
  }, [formData]);

  useEffect(() => {
    void getPageData();
  }, [getPageData]);

  const handleSearch = useCallback(
    (values: SelectCmsCustomerFeedbackPageParams): void => {
      setFormData((current) => ({
        ...current,
        ...values,
        ypageNum: 1,
      }));
    },
    []
  );

  const handleReset = useCallback((): void => {
    setFormData(initialFormData);
  }, []);

  const handlePageChange = useCallback(
    (current: number, pageSize: number): void => {
      setFormData((values) => ({
        ...values,
        ypageNum: current,
        ypageSize: pageSize,
      }));
    },
    []
  );

  const handleOpenEdit = useCallback((row: CustomerFeedbackRow): void => {
    setEditingFeedback({
      id: row.id,
      feedbackResponseType: row.feedbackResponseType,
      feedbackResponseContent: row.feedbackResponseContent,
      remark: row.remark,
    });
    setModalOpen(true);
  }, []);

  const handleCloseEdit = useCallback((): void => {
    setModalOpen(false);
    setEditingFeedback({});
  }, []);

  const handleViewDetail = useCallback(
    (title: string, value: string): void => {
      Modal.info({
        content: (
          <Paragraph
            style={{
              marginBottom: 0,
              maxHeight: "60vh",
              overflow: "auto",
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap",
            }}
          >
            {value}
          </Paragraph>
        ),
        icon: null,
        okText: "关闭",
        title,
        width: 640,
      });
    },
    []
  );

  const handleEdit = useCallback(
    async (values: EditCustomerFeedbackParams): Promise<void> => {
      setModalLoading(true);
      try {
        await editCustomerFeedback({
          id: editingFeedback.id,
          feedbackResponseType: values.feedbackResponseType,
          feedbackResponseContent:
            values.feedbackResponseType === 1
              ? values.feedbackResponseContent
              : "",
          remark: values.remark,
        });
        message.success("回复成功");
        handleCloseEdit();
        await getPageData();
      } finally {
        setModalLoading(false);
      }
    },
    [editingFeedback.id, getPageData, handleCloseEdit]
  );

  const formTab = useMemo(
    () => [
      {
        label: "用户昵称",
        name: "nickName",
        type: "input",
      },
      {
        label: "用户账号",
        name: "mobile",
        type: "input",
      },
      {
        label: "反馈状态",
        name: "status",
        type: "select",
        options: statusOptions,
      },
      {
        label: "反馈日期",
        name: "rangePicker",
        type: "rangePicker",
        width: "380px",
      },
    ],
    []
  );

  const modalFormTab = useMemo(
    () => [
      {
        label: "回复方式",
        labelCol: { span: 4 },
        name: "feedbackResponseType",
        type: "radio",
        options: responseTypeOptions,
        rules: [{ required: true, message: "请选择回复方式" }],
        width: "100%",
        wrapperCol: { span: 20 },
      },
      {
        label: "回复内容",
        labelCol: { span: 4 },
        name: "feedbackResponseContent",
        type: "textArea",
        showCondition:
          "typeof feedbackResponseType !== 'undefined' && feedbackResponseType === 1",
        rules: [{ required: true, message: "请输入回复内容" }],
        params: {
          rows: 5,
          style: { width: "100%" },
        },
        width: "100%",
        wrapperCol: { span: 20 },
      },
      {
        label: "备注",
        labelCol: { span: 4 },
        name: "remark",
        type: "textArea",
        params: {
          rows: 4,
          style: { width: "100%" },
        },
        width: "100%",
        wrapperCol: { span: 20 },
      },
    ],
    []
  );

  const columns = useMemo<ColumnsType<CustomerFeedbackRow>>(
    () => [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
      },
      {
        title: "用户业务ID",
        dataIndex: "userBusinessId",
        key: "userBusinessId",
      },
      {
        title: "反馈用户ID",
        dataIndex: "userId",
        key: "userId",
      },
      {
        title: "用户昵称",
        dataIndex: "nickName",
        key: "nickName",
      },
      {
        title: "用户账号",
        dataIndex: "mobile",
        key: "mobile",
      },
      {
        title: "用户反馈",
        dataIndex: "customerFeedback",
        key: "customerFeedback",
        width: 240,
        render: (value: string): React.ReactNode => (
          <DetailTextCell
            title="用户反馈详情"
            value={value}
            onView={handleViewDetail}
          />
        ),
      },
      {
        title: "反馈图片",
        key: "customerFeedbackImgList",
        render: (_value: unknown, row: CustomerFeedbackRow): React.ReactNode => {
          const urls = parseImageUrls(row);
          return urls.length > 0 ? (
            <Image.PreviewGroup>
              <Space>
                {urls.map((url) => (
                  <Image key={url} width={56} height={56} src={url} />
                ))}
              </Space>
            </Image.PreviewGroup>
          ) : (
            "-"
          );
        },
      },
      {
        title: "联系电话",
        dataIndex: "customerMobile",
        key: "customerMobile",
      },
      {
        title: "回复方式",
        dataIndex: "feedbackResponseType",
        key: "feedbackResponseType",
        render: (value: number): React.ReactNode =>
          responseTypeOptions.find((item) => item.value === value)?.label ?? "-",
      },
      {
        title: "回复内容",
        dataIndex: "feedbackResponseContent",
        key: "feedbackResponseContent",
        width: 240,
        render: (value: string): React.ReactNode => (
          <DetailTextCell
            title="回复内容详情"
            value={value}
            onView={handleViewDetail}
          />
        ),
      },
      {
        title: "反馈状态",
        dataIndex: "status",
        key: "status",
        render: (value: number): React.ReactNode => (
          <Tag color={value === 1 ? "green" : "orange"}>
            {value === 1 ? "已处理" : "待处理"}
          </Tag>
        ),
      },
      {
        title: "备注",
        dataIndex: "remark",
        key: "remark",
        width: 240,
        render: (value: string): React.ReactNode => (
          <DetailTextCell
            title="备注详情"
            value={value}
            onView={handleViewDetail}
          />
        ),
      },
      {
        title: "反馈时间",
        dataIndex: "createTime",
        key: "createTime",
        render: formatTime,
      },
      {
        title: "回复时间",
        dataIndex: "updateTime",
        key: "updateTime",
        render: formatTime,
      },
      {
        title: "操作",
        key: "operation",
        fixed: "right",
        render: (_value: unknown, row: CustomerFeedbackRow): React.ReactNode => (
          <Button
            icon={setIcons("EditOutlined")}
            size="small"
            type="text"
            onClick={() => handleOpenEdit(row)}
          >
            {row.status === 1 ? "修改回复" : "回复"}
          </Button>
        ),
      },
    ],
    [handleOpenEdit, handleViewDetail]
  );

  return (
    <div>
      <Modal
        destroyOnHidden
        footer={null}
        open={modalOpen}
        title={editingFeedback.id ? "回复用户反馈" : "回复"}
        width={720}
        onCancel={handleCloseEdit}
      >
        <PageTab
          confirmLoading={modalLoading}
          formTab={modalFormTab}
          initialValues={editingFeedback}
          layout="horizontal"
          reset={handleCloseEdit}
          search={handleEdit}
          subFoot
        />
      </Modal>
      <PageTab
        defaultTimer={[]}
        footer={[]}
        formTab={formTab}
        reset={handleReset}
        search={handleSearch}
      />
      <PageTable
        columns={columns}
        data={pageData}
        loading={loading}
        pageChange={handlePageChange}
        pagination={{
          current: formData.ypageNum,
          pageSize: formData.ypageSize,
          total,
        }}
        rowKey="id"
      />
    </div>
  );
};

export default CustomerFeedbackManager;
